import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Typography,
  TextField,
  IconButton,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Container,
  Box,
  Button,
  Autocomplete,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import { toast } from "react-toastify";
import { PartnerArrowIcon } from "../../res/NewWebIcons";

const BankChargesTable = () => {
  const [rows, setRows] = useState([]);
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState("INSTANTPAY");
  const [userRole, setUserRole] = useState(null);
  const [partnerList, setPartnerList] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [searchInput, setSearchInput] = useState(""); 
  const token = sessionStorage.getItem("jwtToken");

  // Axios instance with the token in the headers
  const axiosInstance = axios.create({
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  useEffect(() => {
    fetchProviders();
    handleProviderChange({ target: { value: "INSTANTPAY" } });
  }, []);

  const fetchPartners = async (role) => {
    try {
      const response = await axiosInstance.get(
        `https://api.ipaisa.co.in/myapp/admin/getAllPartner?userrole=${role}`
      );
      const partners = response.data || [];
      setPartnerList(partners);
      if (partners.length > 0) {
        setSelectedPartner(partners[0]);
      } else {
        setSelectedPartner(null);
      }
    } catch (error) {
      console.error("Error fetching partners:", error);
      toast.error("Failed to fetch partner list");
    }
  };

  const fetchProviders = async () => {
    try {
      const response = await axiosInstance.get(
        "https://api.ipaisa.co.in/myapp/admin/getproviderlist"
      );
      setProviders(response.data.data || []); // Adjusted based on the API response structure
    } catch (error) {
      console.error("Error fetching providers:", error);
    }
  };

  const handleRoleChange = (event) => {
    const role = event.target.value;
    setUserRole(role);
    fetchPartners(role);
  };

  const handlePartnerChange = (event) => {
    setSelectedPartner(event.target.value);
  };

  const handleSubmit = async () => {
    if (!selectedPartner) {
      toast.error("Please select a partner");
      return;
    }

    if (!selectedProvider) {
      toast.error("Please select a provider");
      return;
    }

    try {
      const response = await axiosInstance.get(
        `https://api.ipaisa.co.in/myapp/admin/getByUser/${selectedProvider}/${selectedPartner.userId}`
      );

      // Process the response data
      const fetchedRows = response.data.map((item, index) => ({
        srNo: index + 1,
        transactionType: item.serviceName,
        transactionSlab: item.transactionSlab,
        bankCharges: item.payoutCharge,
        provider: item.provider,
        payoutChargesId: item.payoutChargesId, // Add this line
        isEditing: false,
      }));


      setRows(fetchedRows);
      toast.success(`Charges loaded for ${selectedPartner.Name} (${selectedPartner.userId})`);
      
    } catch (error) {
      console.error("Error fetching charges:", error);
      toast.error("Failed to fetch charges for selected partner");
    }
  };


const handleCancel = async () => {
  try {
    // Reset partner selection
    setUserRole(null);
    setSelectedPartner(null);
    setSearchInput("");
    
    // Reload the global provider data
    const response = await axiosInstance.get(
      `https://api.ipaisa.co.in/myapp/admin/getprovider/${selectedProvider}`
    );

    const fetchedRows = response.data.map((item, index) => ({
      srNo: index + 1,
      transactionType: item.service.transactionType,
      transactionSlab: item.service.transactionSlab,
      bankCharges: item.bankChargesl,
      provider: item.provider.providerName,
      isEditing: false,
    }));

    setRows(fetchedRows);
    toast.success("Returned to global charges view");
    
  } catch (error) {
    console.error("Error resetting to global view:", error);
    toast.error("Failed to load global charges");
  }
};


const handleProviderChange = async (event) => {
  const provider = event.target.value;
  setSelectedProvider(provider);

  // Only fetch global data if no partner is selected
  if (!selectedPartner) {
    try {
      const response = await axiosInstance.get(
        `https://api.ipaisa.co.in/myapp/admin/getprovider/${provider}`
      );

      const fetchedRows = response.data.map((item, index) => ({
        srNo: index + 1,
        transactionType: item.service.transactionType,
        transactionSlab: item.service.transactionSlab,
        bankCharges: item.bankChargesl,
        provider: item.provider.providerName,
        isEditing: false,
      }));

      setRows(fetchedRows);
    } catch (error) {
      console.error("Error fetching data for selected provider:", error);
    }
  }
};


  const handleEdit = (srNo) => {
    setRows(
      rows.map((row) => (row.srNo === srNo ? { ...row, isEditing: true } : row))
    );
  };

const handleSave = async (srNo, newUserCharges, newProvider) => {
  try {
    const selectedRow = rows.find((row) => row.srNo === srNo);

    // User-specific flow (when partner is selected)
    if (selectedPartner) {
      const payload = {
        payoutChargesId: selectedRow.payoutChargesId, // Added from API response
        payoutCharge: newUserCharges
      };

      const response = await axiosInstance.put(
        "https://api.ipaisa.co.in/myapp/admin/updateUserPayoutCharges",
        payload
      );

      if (response.data.message === "Charges Updated Successfully") {
        toast.success("User charges updated successfully!");
        setRows(rows.map(row => 
          row.srNo === srNo ? { 
            ...row, 
            bankCharges: newUserCharges,
            isEditing: false 
          } : row
        ));
      } else {
        toast.error("Failed to update user charges");
      }
    } 
    // Global flow (when no partner is selected)
    else {
      const payload = {
        provider: newProvider,
        transactionType: selectedRow.transactionType,
        transactionSlab: selectedRow.transactionSlab,
        amount: newUserCharges,
      };

      const response = await axiosInstance.put(
        "https://api.ipaisa.co.in/myapp/admin/setcharge",
        payload
      );

      if (response.data.status === "Charges set successfully") {
        toast.success("Global charges updated successfully!");
        setRows(rows.map(row => 
          row.srNo === srNo ? { 
            ...row, 
            bankCharges: newUserCharges,
            provider: newProvider,
            isEditing: false 
          } : row
        ));
      } else {
        toast.error("Failed to update global charges");
      }
    }
  } catch (error) {
    console.error("Error updating charges:", error);
    toast.error("An error occurred while updating charges");
  }
};


  const handleChange = (srNo, key, value) => {
    setRows(
      rows.map((row) => (row.srNo === srNo ? { ...row, [key]: value } : row))
    );
  };

  return (
    <Box style={{ padding: "2rem", marginTop: "calc(3% + 56px)" }}>
      <Grid
        style={{
          display: "flex",
          alignItems: "center",
          gap: "1%",
          justifyContent: "start",
        }}
      >
        <Typography
          color="textSecondary"
          style={{ fontFamily: "sans-serif", fontSize: "14px" }}
        >
          Balance
        </Typography>
        <PartnerArrowIcon />
        <Typography
          color="textSecondary"
          style={{ fontFamily: "sans-serif", fontSize: "14px" }}
        >
          Bank Charges
        </Typography>
      </Grid>
      <Grid
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: "1%",
        }}
      >
        <Grid style={{ marginTop: "1%" }}>
          <Typography
            style={{
              fontWeight: "bold",
              fontFamily: "sans-serif",
              fontSize: "19px",
              color: "#343C6A",
            }}
          >
            Bank Charges
          </Typography>
        </Grid>
      </Grid>
      <Paper sx={{padding:'1.5rem', marginTop:'2%', boxShadow:'none', borderRadius:'10px'}}>
        <Grid container spacing={2} style={{ marginTop: "1rem" }}>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>User Role</InputLabel>
              <Select
                value={userRole}
                onChange={handleRoleChange}
                label="User Role"
              >
                <MenuItem value="AREADISTRIBUTOR">Area Distributor</MenuItem>
                <MenuItem value="RETAILER">Retailer</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <Autocomplete
              options={partnerList}
              getOptionLabel={(option) => `${option.Name} (${option.userId})`}
              value={selectedPartner}
              onChange={(event, newValue) => {
                setSelectedPartner(newValue);
              }}
              inputValue={searchInput}
              onInputChange={(event, newInputValue) => {
                setSearchInput(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Partner"
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              )}
              filterOptions={(options, state) => {
                return options.filter(option =>
                  option.Name.toLowerCase().includes(state.inputValue.toLowerCase()) ||
                  option.userId.toLowerCase().includes(state.inputValue.toLowerCase())
                );
              }}
              disabled={!userRole}
            />
          </Grid>

              <Grid item xs={12} md={4} style={{ display: 'flex', gap: '1rem' }}>
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={handleSubmit}
                  style={{ flex: 1 }}
                >
                  Submit
                </Button>
                <Button 
                  variant="outlined" 
                  color="secondary"
                  onClick={handleCancel}
                  style={{ flex: 1 }}
                >
                  Cancel
                </Button>
              </Grid>

                  {/* Add Dropdown Here */}
          <Grid item xs={12} md={3}>
          <FormControl
            variant="outlined"
            size="small"
            style={{ minWidth: 200 }}
          >
            <InputLabel>Provider</InputLabel>
            <Select
              value={selectedProvider}
              onChange={handleProviderChange}
              label="Provider"
            >
              {providers.map((provider) => (
                <MenuItem key={provider.id} value={provider.providerName}>
                  {provider.providerName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          </Grid>
        </Grid>
        
      </Paper>
      <Grid style={{ marginTop: "2%" }}>
        <TableContainer
          component={Paper}
          style={{ backgroundColor: "#f4f7fc", borderRadius: "10px" }}
        >
          <Table>
            <TableHead>
              <TableRow style={{ backgroundColor: "#0d6efd" }}>
                <TableCell style={{ color: "#fff" }}>Sr No</TableCell>
                <TableCell style={{ color: "#fff" }}>
                  Transaction Type
                </TableCell>
                <TableCell style={{ color: "#fff" }}>
                  Transaction Slab
                </TableCell>
                <TableCell style={{ color: "#fff" }}>Bank Charges</TableCell>
                <TableCell style={{ color: "#fff" }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow
                  key={row.srNo}
                  style={{
                    backgroundColor: index % 2 === 1 ? "#D0EFFF" : "#fff",
                  }}
                >
                  <TableCell>{row.srNo}</TableCell>
                  <TableCell>{row.transactionType}</TableCell>
                  <TableCell>{row.transactionSlab}</TableCell>
                  <TableCell>
                    {row.isEditing ? (
                      <TextField
                        type="number"
                        value={row.bankCharges}
                        onChange={(e) =>
                          handleChange(row.srNo, "bankCharges", e.target.value)
                        }
                        size="small"
                        variant="outlined"
                        style={{ marginRight: "10px" }}
                      />
                    ) : (
                      row.bankCharges
                    )}
                  </TableCell>
                  <TableCell>
                    {row.isEditing ? (
                      <IconButton
                        onClick={() =>
                          handleSave(row.srNo, row.bankCharges, row.provider)
                        }
                      >
                        <SaveIcon color="primary" />
                      </IconButton>
                    ) : (
                      <IconButton onClick={() => handleEdit(row.srNo)}>
                        <EditIcon color="action" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </Box>
  );
};

export default BankChargesTable;
